import Cookies from 'js-cookie';
import get from 'lodash.get';

import config from '../config';
import apiClient from '../middleware/apiClient';

const {
  accountSwitcherCookieName,
  legacyAccountSwitcherCookieName,
  accountSwitcherCookieDomain,
} = config();

export const getIsDisconnected = ({ status } = {}) => status === 'DISCONNECTED';

// Read from a cookie to select the correct account
// and set the CSP auth header appropriately.
export const setCspAccountToken = ({ accounts = [] }, index) => {
  const selectedAccount = accounts[index];

  if (selectedAccount) {
    const { cspToken } = selectedAccount;
    apiClient.setHeaders({ 'CSP-Authorization': `LoginToken ${cspToken}` });
  }
};

export const getAccountIndex = ({ accounts = [] }) => {
  const selectedIndex = accounts.findIndex(({ isSelectedAccount }) => !!isSelectedAccount);
  return selectedIndex > -1 ? selectedIndex : 0;
};

export const setAccountIndex = (accountIndex, validate = true) => {
  // Guard against the accountIndex becoming out of sync with the value stored in the cookie
  // possibly because of multiple tabs being open with different accounts selected.
  if (validate) {
    const cookieIndex = parseInt(Cookies.get(accountSwitcherCookieName), 10);

    if (Number.isInteger(cookieIndex) && accountIndex !== cookieIndex) {
      // If the account index doesn't match what's in the cookie then reload the app.
      window.location.reload();
      throw new Error(`setAccountIndex called with ${accountIndex} does not match cookie value ${cookieIndex}`);
    }
  }
  Cookies.set(
    accountSwitcherCookieName,
    accountIndex.toString(10),
    {
      domain: accountSwitcherCookieDomain,
      expires: 1, // 1 day from now, js-cookie supports this shorthand
    },
  );

  // Temporarily set the legacy account switcher cookie for a smooth rollout
  // until account selection changes are deployed for all frontend apps
  // TODO: Remove this call when maw_cspTokenIndex is cleaned up
  Cookies.set(
    legacyAccountSwitcherCookieName,
    accountIndex.toString(10),
    {
      domain: accountSwitcherCookieDomain,
      expires: 1, // 1 day from now, js-cookie supports this shorthand
    },
  );
};

// TODO: Remove this helper when maw_cspTokenIndex is cleaned up
export const checkForLegacyAccountCookie = () => {
  const legacyCookie = Cookies.get(legacyAccountSwitcherCookieName);
  if (legacyCookie) {
    setAccountIndex(legacyCookie, false);
  }
};

export const getAccountPerms = (macaroon, accountIndex = 0) => {
  if (!macaroon || !macaroon.accounts || !macaroon.accounts.length) {
    return [];
  }

  const { accounts } = macaroon;

  let selAccount = accounts.find(acc => acc && acc.isSelectedAccount);

  if (!selAccount) {
    const hasValidIndex = accountIndex >= 0 && accountIndex < accounts.length;

    // Invalid index — fail fast to avoid selecting unintended account
    if (!hasValidIndex) {
      throw new Error(`Invalid accountIndex: ${accountIndex}`);
    }

    selAccount = accounts[accountIndex];
  }

  return get(selAccount, 'roles') || [];
};

export const formatAccountNumber = accountNumber => String(accountNumber).replace(/(\d{4})/g, '$1 ').trim();

export const sortCurrentAccountFirst = (accounts = [], currentAccountNumber) => (
  accounts.reduce((sortedAccounts, account) => {
    if (account.accountNumber === currentAccountNumber) {
      sortedAccounts.unshift(account);
    } else {
      sortedAccounts.push(account);
    }

    return sortedAccounts;
  }, [])
);

export const getDeviceFromAccount = ({ services }) => (
  Object.keys(services).reduce((acc, service) => {
    const { devices } = services[service];

    if (devices) {
      return acc.concat(devices.map(device => ({
        ...device,
        lob: service,
      })));
    }
    return acc;
  }, [])
);

export const getBillPayAccounts = (accounts = []) => (
  accounts.filter(({ roles = [] }) => (
    roles.indexOf('BILLPAY') > -1
    || roles.indexOf('PRIMARY') > -1
  ))
);

export const hasActiveDevice = (account, lob) => (
  (get(account, `services.${lob}.devices`) || [])
    .some(device => device.activationStatus === 'ACTIVE')
);

export const getLOBs = ({ services = {} } = {}) => (
  ['internet', 'video', 'voice', 'home', 'mobile', 'stream', 'storeFront'].reduce((acc, type) => ({
    ...acc,
    [type]: type === 'stream'
      ? get(services, 'video.instantTV')
      : Boolean(services[type] && services[type].subscribed),
  }), {})
);
